import { Box, Skeleton } from '@mui/material';
import agent from '@root/agent';
import CircularCloseButton from '@root/components/v2/ui-elements/CircularCloseButton';
import { Switch } from '@root/components/v3/switch';
import { useMutation, useQuery } from '@tanstack/react-query';

import { ArrowLeft, ArrowRight, Check, CheckCircle, CloudOff, Copy, HelpCircle, Terminal, X } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { toast } from 'sonner';

const steps = [
  {
    id: 0,
    title: 'Check Cluster Addons',
    description: 'Verify the required addons',
    icon: (
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1e293b',
          color: 'white',
        }}>
        1
      </div>
    ),
  },
  {
    id: 1,
    title: 'Run Eligibility Script',
    description: 'Verify cluster compatibility',
    icon: (
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1e293b',
          color: 'white',
        }}>
        2
      </div>
    ),
  },

  {
    id: 2,
    title: 'Execute Deployment Commands',
    description: 'Complete the installation',
    icon: (
      <div
        style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#1e293b',
          color: 'white',
        }}>
        3
      </div>
    ),
  },
];

const styles = {
  wrapper: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 15px',
    paddingBottom: '15px',
  },
  header: {
    textAlign: 'left',
    marginBottom: '15px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a202c',
    marginBottom: '8px',
    margin: 0,
  },
  subtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  stepIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  stepIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
    marginBottom: '8px',
  },
  stepIconCompleted: {
    backgroundColor: '#10b981',
    color: 'white',
    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.25)',
  },
  stepIconActive: {
    backgroundColor: '#1e293b',
    color: 'white',
    boxShadow: '0 10px 25px rgba(59, 130, 246, 0.25)',
  },
  stepIconInactive: {
    backgroundColor: '#e5e7eb',
    color: '#6b7280',
  },
  stepText: {
    textAlign: 'center',
    // maxWidth: '96px',
  },
  stepTitle: {
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '4px',
  },
  stepDescription: {
    fontSize: '12px',
    color: '#6b7280',
  },
  stepConnector: {
    width: '64px',
    height: '2px',
    margin: '0 16px',
    transition: 'background-color 0.3s ease',
  },
  stepConnectorCompleted: {
    backgroundColor: '#10b981',
  },
  stepConnectorIncomplete: {
    backgroundColor: '#e5e7eb',
  },
  contentCard: {
    maxWidth: '1024px',
    margin: '0 auto',
    backgroundColor: 'white',
    borderRadius: '16px',
    // boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)',
    border: '1px solid #f3f4f6',
    padding: '15px 30px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  cardIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
  },
  cardIconBlue: {
    backgroundColor: '#dbeafe',
  },
  cardIconGreen: {
    backgroundColor: '#dbeafe',
  },
  cardIconPurple: {
    backgroundColor: '#dbeafe',
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1a202c',
    margin: 0,
  },
  cardSubtitle: {
    color: '#6b7280',
    fontSize: '14px',
    margin: 0,
  },
  alertBox: {
    padding: '16px',
    borderRadius: '12px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  alertBoxError: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
  },
  alertBoxInfo: {
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
  },
  alertBoxSuccess: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
  },
  alertIcon: {
    width: '20px',
    height: '20px',
    marginTop: '2px',
    flexShrink: 0,
  },
  alertTitle: {
    fontWeight: '500',
    marginBottom: '4px',
  },
  alertText: {
    fontSize: '14px',
  },
  commandBlock: {
    position: 'relative',
  },
  commandTitle: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '8px',
  },
  commandContainer: {
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    maxHeight: '200px',
  },
  commandHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: 'rgba(55, 65, 81, 0.8)',
    borderBottom: '1px solid #374151',
  },
  commandHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  commandHeaderTitle: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#d1d5db',
  },
  copyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    color: '#d1d5db',
    backgroundColor: '#374151',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  copyButtonHover: {
    color: 'white',
    backgroundColor: '#4b5563',
  },
  commandPre: {
    padding: '16px',
    fontSize: '13px',
    color: '#f3f4f6',
    fontFamily: 'Manrope',
    overflowX: 'auto',
    lineHeight: '1.5',
    margin: 0,
    whiteSpace: 'pre-wrap',
    maxHeight: '120px',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '32px',
  },
  radioOption: {
    padding: '24px',
    borderRadius: '12px',
    border: '2px solid',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  radioOptionSelected: {
    borderColor: '#3b82f6',
    backgroundColor: '#eff6ff',
    boxShadow: '0 10px 25px rgba(59, 130, 246, 0.1)',
  },
  radioOptionUnselected: {
    borderColor: '#e5e7eb',
    backgroundColor: '#f9fafb',
  },
  radioContent: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
  },
  radioButton: {
    width: '20px',
    height: '20px',
    marginTop: '4px',
    borderRadius: '50%',
    border: '2px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  radioButtonSelected: {
    borderColor: '#1e293b',
    backgroundColor: '#1e293b',
  },
  radioButtonUnselected: {
    borderColor: '#d1d5db',
    backgroundColor: 'transparent',
  },
  radioButtonDot: {
    width: '8px',
    height: '8px',
    backgroundColor: 'white',
    borderRadius: '50%',
  },
  radioText: {
    flex: 1,
  },
  radioTitle: {
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: '8px',
  },
  radioDescription: {
    fontSize: '14px',
    color: '#6b7280',
    marginBottom: '12px',
  },
  badgeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  badge: {
    padding: '4px 8px',
    fontSize: '12px',
    borderRadius: '9999px',
    fontWeight: '500',
  },
  badgeGreen: {
    backgroundColor: '#dcfce7',
    color: '#166534',
  },
  badgeBlue: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
  },
  badgeGray: {
    backgroundColor: '#f3f4f6',
    color: '#4b5563',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '32px',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '12px',
    fontWeight: '500',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
    fontFamily: 'Manrope',
    height: '45px',
  },
  buttonPrimary: {
    backgroundColor: '#1e293b',
    color: 'white',
  },
  buttonPrimaryHover: {
    backgroundColor: '#334155',
  },
  buttonSecondary: {
    backgroundColor: 'transparent',
    color: '#374151',
    border: '1px solid #d1d5db',
  },
  buttonSecondaryHover: {
    backgroundColor: '#f9fafb',
  },
  buttonSuccess: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.25)',
  },
  buttonSuccessHover: {
    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
  },
  buttonDisabled: {
    backgroundColor: '#d1d5db',
    color: '#9ca3af',
    cursor: 'not-allowed',
  },
  skeletonContainer: {
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  },
  skeleton: {
    height: '128px',
    backgroundColor: '#e5e7eb',
    borderRadius: '12px',
    width: '800px',
  },
  transition: {
    transition: 'all 0.5s ease-in-out',
  },
  spacer: {
    marginBottom: '24px',
  },
  spanCopy: {
    fontSize: '12px',
    fontWeight: '500',

    fontFamily: 'Manrope',
  },
  switchContainer: {
    padding: '16px 0',
    marginBottom: '16px',
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    gap: '8px',
  },
  switchLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  apiSecPanel: {
    padding: '16px',
    marginBottom: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#f9fafb',
  },
  apiSecLabel: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
    marginBottom: '4px',
    marginTop: '12px',
  },
  apiSecHint: {
    fontSize: '12px',
    color: '#6b7280',
    margin: '-6px 0 0 0',
  },
  textInput: {
    width: '100%',
    padding: '8px 10px',
    fontSize: '13px',
    fontFamily: 'inherit',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '8px',
    boxSizing: 'border-box',
  },
  apiSecButton: {
    marginTop: '16px',
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#ffffff',
    backgroundColor: '#111827',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
};

export default function ClusterOnboarding({
  selectedClusterRowData,
  selectedRowData,
  updateEnvironment,
  isConnected,
  handleDisconnectCluster,
  disconnectLoaded,
  handleClosePopup,
  errorK8Value,
  environmentData,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [copiedStates, setCopiedStates] = useState({});
  const [addOnList, setaddOnList] = useState([]);
  const [cloudFormationOption, setCloudFormation] = React.useState(false);
  const [isGeneratingCommand] = useState(false);
  const [errorValue, setErrorValue] = useState({ error: false, message: '' });
  const [acknowledgeCustomAuth, setAcknowledgeCustomAuth] = useState(false);
  // API Security (T-1). `apiSecForm` holds what the user is TYPING; `appliedApiSec` holds what they
  // committed with the Generate button. The two are separate on purpose: the react-query key below
  // includes the committed payload, so binding it to the raw inputs would refetch the helm command
  // on every keystroke.
  const [apiSecurityEnabled, setApiSecurityEnabled] = useState(false);
  const [apiSecForm, setApiSecForm] = useState({
    namespace: '',
    serviceName: '',
    storageClass: 'gp2',
    storageSize: '10Gi',
  });
  const [appliedApiSec, setAppliedApiSec] = useState(null);

  // Mapping for add-on descriptions
  const addOnDescriptions = {
    workload_identity_enabled:
      'Your cluster should use Workload Identity to let applications securely access Google Cloud services without storing credentials',
    workloadMetadataConfigMode:
      'Configure how workloads access instance metadata to prevent unauthorized access to sensitive information',
    'workloadMetadataConfig.mode':
      'Configure how workloads access instance metadata to prevent unauthorized access to sensitive information',
    workload_metadata_config_mode:
      'Configure how workloads access instance metadata to prevent unauthorized access to sensitive information',
    serviceAccount: 'Your cluster is using a proper service account for authentication',
    service_account: 'Your cluster is using a proper service account for authentication',
    logging_enabled: 'Cluster logging is active, helping you monitor and troubleshoot issues',
    loggingEnabled: 'Cluster logging is active, helping you monitor and troubleshoot issues',
    k8_version: 'Your cluster is running a supported Kubernetes version with the latest security patches',
    k8Version: 'Your cluster is running a supported Kubernetes version with the latest security patches',
    kubernetes_version: 'Your cluster is running a supported Kubernetes version with the latest security patches',
  };

  // Check if workload identity or metadata config failed
  const criticalChecksFailed = useMemo(() => {
    const workloadIdentity = addOnList.find(addon => {
      const name = addon.name.toLowerCase();
      return (
        name === 'workload_identity_enabled' ||
        name === 'workload identity enabled' ||
        name.includes('workload_identity')
      );
    });
    const workloadMetadata = addOnList.find(addon => {
      const name = addon.name.toLowerCase();
      return (
        name === 'workloadmetadataconfig.mode' ||
        name === 'workload_metadata_config_mode' ||
        name.includes('workloadmetadataconfig') ||
        name.includes('workload metadata config')
      );
    });

    return (workloadIdentity && !workloadIdentity.active) || (workloadMetadata && !workloadMetadata.active);
  }, [addOnList]);

  // console.log(addOnList, 'addOnList');

  const { data: addOnData, isFetching: isFetchingAddOnData } = useQuery(
    [
      'get_add_on_data',
      activeStep,
      selectedClusterRowData?.cluster_name,
      selectedClusterRowData?.region?.[0] ?? '',
      selectedClusterRowData?.account_id,
    ],
    () =>
      agent.Kubernetes.check_eligibility({
        account_id: selectedRowData?.account_id,
        cluster_name: selectedRowData?.cluster_name,
        cluster_region: selectedRowData.region[0],
        cloud_provider: selectedRowData?.cloud_provider,
      }),
    {
      enabled: activeStep === 0,
    },
  );

  const { data: command, isFetching: isEligibilityLoading } = useQuery(
    ['eligibility-command', activeStep, selectedRowData?.cloud_provider],
    () =>
      agent.Edit_Enviroment.check_eligibility({
        cloud_provider: selectedRowData?.cloud_provider,
      }),
    {
      enabled: activeStep === 1,
      select: res => res?.data?.command,
      retry: false,
    },
  );

  const { data: helmData, isFetching: isHelmLoading } = useQuery(
    [
      'get_helm_command',
      activeStep,
      selectedClusterRowData?.cluster_name,
      selectedClusterRowData?.region?.[0] ?? '',
      selectedClusterRowData?.account_id,
      selectedClusterRowData?.cluster_id,
      // The committed API Security payload MUST be part of the key. Without it react-query serves
      // the cached single-chart command after the user enables API Security, and the second block
      // never appears — with no error to explain why.
      JSON.stringify(appliedApiSec),
    ],
    () =>
      agent.Edit_Enviroment.get_helm_command({
        cluster_name: selectedClusterRowData?.cluster_name,
        cluster_region: selectedClusterRowData?.region?.[0] ?? '',
        account_id: selectedClusterRowData?.account_id,
        cluster_id: selectedClusterRowData?.cluster_id,
        api_security: appliedApiSec,
      }),
    {
      enabled: activeStep === 2,
      // The whole response now, not just .command: the API Security chart arrives as an ADDITIVE
      // second key so an older backend simply yields undefined here and one block renders.
      select: res => res,
      retry: false,
    },
  );
  const helmInstallCommand = helmData?.command;
  const apiSecurityCommand = helmData?.api_security_command;

  const { data: gcpResourceOnboarding, isFetching: isFetchingGCPResourceOnboarding } = useQuery(
    ['generate_command_onboarding', selectedRowData?.account_id, isConnected],
    () =>
      agent.Edit_Enviroment.gcp_onboarding_command({
        onboarding_method: 'manual',
        onboarding_type: 'single account',
        environment_name: environmentData.environment_name,
        environment_tags: environmentData.environment_tags,
        modules: ['eks_enable'],
        account_id: environmentData.account_id,
        list_clusters: [selectedRowData?.cluster],
      }),
    {
      enabled: Boolean(selectedRowData?.cloud_provider === 'gcp' && !isConnected && activeStep == 2),
      retry: false,
    },
  );

  const { data: deployResourcesCommand, isFetching: isDeployResourcesLoading } = useQuery(
    ['generate_command', activeStep, selectedRowData?.cluster, selectedRowData?.account_id, isConnected],
    () =>
      agent.Edit_Enviroment.generate_command({
        account_id: selectedRowData?.account_id,
        action: isConnected,
        modules: ['eks_enable'],
        list_clusters: [selectedRowData?.cluster],
      }),
    {
      enabled: Boolean((activeStep === 2 && selectedRowData?.cluster) || selectedRowData.cloud_provider === 'aws'),
      retry: false,
    },
  );

  const { data: awsCloudFormation, isFetching: isFetchingCloudFormation } = useQuery(
    ['aws_cloud_formation_onboarding', activeStep, selectedRowData?.cluster, selectedRowData?.account_id],
    () =>
      agent.Edit_Enviroment.check_cloud_formation({
        cluster_name: selectedRowData?.cluster_name,
        cluster_region: selectedRowData?.region[0],
        cluster_arn: selectedRowData?.cluster,
        account_id: selectedRowData?.account_id,
      }),
    {
      enabled: Boolean(activeStep === 2 && selectedRowData.cloud_provider === 'aws' && !isConnected),
      retry: false,
    },
  );

  // api-security-t1 (CyberQ SHIELD runtime API security) CloudFormation step. Only meaningful
  // once the user has opted into API Security via the switch below, since it provisions the
  // forwarder/builder IRSA roles that chart needs — asking for it unconditionally would show a
  // deploy step for a capability the client never asked for.
  const { data: apiSecurityT1CloudFormation, isFetching: isFetchingApiSecurityT1CloudFormation } = useQuery(
    ['aws_api_security_t1_cloud_formation_onboarding', activeStep, selectedRowData?.cluster, selectedRowData?.account_id],
    () =>
      agent.Edit_Enviroment.check_api_security_t1_cloud_formation({
        cluster_region: selectedRowData?.region[0],
        cluster_arn: selectedRowData?.cluster,
        account_id: selectedRowData?.account_id,
        cluster_id: selectedRowData?.cluster_id,
      }),
    {
      enabled: Boolean(
        activeStep === 2 && selectedRowData.cloud_provider === 'aws' && !isConnected && apiSecurityEnabled,
      ),
      retry: false,
    },
  );

  const { data: gcpDeboardingCommands, isFetching: isFetchingGcpDeboardingCommands } = useQuery(
    ['generate_command_deboarding', selectedRowData?.account_id, isConnected],
    () =>
      agent.Edit_Enviroment.gcp_deboarding_command({
        account_id: selectedRowData?.account_id,
      }),
    {
      enabled: Boolean(selectedRowData?.cloud_provider === 'gcp' && isConnected),
      retry: false,
    },
  );

  useEffect(() => {
    if (!isFetchingAddOnData) {
      const eligibilityErrorList = addOnData?.data?.details || addOnData?.data?.addon_status;
      let statusList = [];
      // console.log('eligibilityErrorList', eligibilityErrorList);
      if (eligibilityErrorList && typeof eligibilityErrorList === 'object' && !Array.isArray(eligibilityErrorList)) {
        statusList = Object.entries(eligibilityErrorList).map(([name, item], index) => ({
          id: index + 1,
          name,
          active: item.installed,
        }));
      } else if (eligibilityErrorList && Array.isArray(eligibilityErrorList)) {
        statusList = eligibilityErrorList.map((obj, index) => {
          const [[name]] = Object.entries(obj).filter(([key]) => key !== 'eligible');
          return {
            id: index + 1,
            name,
            active: obj.eligible,
          };
        });
      }
      // console.log(statusList, 'statusList');
      setaddOnList(statusList);
      // Reset acknowledgment when addon list changes
      setAcknowledgeCustomAuth(false);
    }
  }, [addOnData?.data?.addon_status, addOnData?.data?.details, isFetchingAddOnData]);

  const cloudFormationItems = useMemo(() => {
    if (isFetchingCloudFormation) return [];
    return Object.entries(awsCloudFormation?.links || {}).map(([key, value]) => ({
      id: key,
      link: value.link,
      title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    }));
  }, [awsCloudFormation?.links, isFetchingCloudFormation]);

  const apiSecurityT1CloudFormationItems = useMemo(() => {
    if (isFetchingApiSecurityT1CloudFormation) return [];
    return Object.entries(apiSecurityT1CloudFormation?.links || {}).map(([key, value]) => ({
      id: `apiSecT1${key}`,
      link: value.link,
      title: `API Security: ${key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`,
    }));
  }, [apiSecurityT1CloudFormation?.links, isFetchingApiSecurityT1CloudFormation]);

  useEffect(() => {
    if (errorK8Value) {
      setErrorValue(errorK8Value);
    }
  }, [errorK8Value]);

  // Enhanced clipboard logic with visual feedback

  const getHelpMutation = useMutation({
    mutationFn: async () => {
      const response = await agent.ClientDashboard.get_help();
      const url = response?.data?.signedUrl;

      if (!url) throw new Error('No help url found.');

      window.open(url, '_blank');
    },
    onError: async e => {
      toast.error(`Failed to open Help: ${e?.message || ''}`);
    },
  });

  const handleHelpClick = useCallback(() => {
    getHelpMutation.mutate();
  }, [getHelpMutation]);

  // Step navigation
  const handleNext = () => setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  const handleBack = () => setActiveStep(prev => Math.max(prev - 1, 0));

  // Modern Command Block Component

  // Step Indicator Component
  const StepIndicator = () => (
    <div style={styles.stepIndicator}>
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === activeStep;
        const isCompleted = index < activeStep;

        return (
          <React.Fragment key={step.id}>
            <div style={styles.stepItem}>
              <div
                style={{
                  ...styles.stepIcon,
                  ...(isCompleted
                    ? styles.stepIconCompleted
                    : isActive
                    ? styles.stepIconActive
                    : styles.stepIconInactive),
                }}>
                {isCompleted ? <CheckCircle size={20} /> : Icon}
              </div>
              <div style={styles.stepText}>
                <div
                  style={{
                    ...styles.stepTitle,
                    color: isActive ? '#3b82f6' : isCompleted ? '#10b981' : '#6b7280',
                  }}>
                  {step.title}
                </div>
                <div style={styles.stepDescription}>{step.description}</div>
              </div>
            </div>
            {index < steps.length - 1 && (
              <div
                style={{
                  ...styles.stepConnector,
                  ...(index < activeStep ? styles.stepConnectorCompleted : styles.stepConnectorIncomplete),
                }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  // console.log('eligibilityData', eligibilityData, deployResourcesCommand, addOnList);

  // Step 1: Cluster Addons UI
  const renderClusterAddons = () => (
    <div style={styles.contentCard}>
      <div style={styles.cardHeader}>
        <div style={{ ...styles.cardIcon, ...styles.cardIconBlue }}>1</div>
        <div>
          <p style={styles.cardTitle}>Check Cluster Addons</p>
          <p style={styles.cardSubtitle}>Verify your cluster has the required addons</p>
        </div>
      </div>
      <>
        <Box>
          {isFetchingAddOnData ? (
            <Box margin={'10px 0px'}>
              <Skeleton variant="rectangular" height={'200px'} width={'100%'} sx={{ borderRadius: '15px' }} />
            </Box>
          ) : addOnList.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                margin: '10px 0px',
                width: '100%',
              }}>
              {addOnList.map(addon => {
                const isActive = addon.active;
                const description = addOnDescriptions[addon.name] || `Check status for ${addon.name}`;

                return (
                  <div
                    key={addon.id}
                    style={{
                      padding: '15px',
                      borderRadius: '8px',
                      color: isActive ? 'rgb(16, 185, 129)' : '#f87171',
                      border: 'none',
                      background: isActive ? '#e6f9ed' : '#fde8e8',
                      transition: 'all 0.2s',
                      cursor: 'pointer',
                      boxShadow: isActive ? '0 2px 8px 0 rgba(52,211,153,0.08)' : '0 2px 8px 0 rgba(248,113,113,0.08)',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        marginBottom: '0px',
                        gap: '12px',
                      }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', color: '#374151', lineHeight: '1.5' }}>{description}</div>
                      </div>
                      <div
                        style={{
                          borderRadius: '50%',
                          background: isActive ? '#34d399' : '#f87171',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: isActive
                            ? '0 1px 4px 0 rgba(52,211,153,0.15)'
                            : '0 1px 4px 0 rgba(248,113,113,0.15)',
                          flexShrink: 0,
                        }}>
                        {addon.active ? <Check size={16} color="#16a34a" /> : <X size={16} color="#dc2626" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={styles.cardSubtitle}>No Addons Available</p>
          )}
          {errorValue.message && (
            <Box
              display="flex"
              height="max-content"
              border="none"
              padding="15px"
              alignItems="flex-start"
              margin="15px 0px"
              gap="4px"
              wordBreak="break-word"
              backgroundColor="#fdd3d3"
              borderRadius="15px"
              minWidth="450px">
              <svg style={{ marginTop: '2px', flexShrink: 0 }} width="16" height="16" viewBox="0 0 18 18" fill="red">
                <path
                  fillRule="evenodd"
                  d="M8 1.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13zM0 8a8 8 0 1116 0A8 8 0 010 8zm9 3a1 1 0 11-2 0 1 1 0 012 0zm-.25-6.25a.75.75 0 00-1.5 0v3.5a.75.75 0 001.5 0v-3.5z"
                />
              </svg>
              <Box
                display="flex"
                fontFamily="Manrope"
                fontSize="13px"
                color="#f84d4d"
                style={{
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  maxWidth: 'calc(100% - 24px)', // Adjust for icon width
                  whiteSpace: 'pre-wrap',
                }}>
                {errorValue.message}
              </Box>
            </Box>
          )}

          {criticalChecksFailed && (
            <Box
              style={{
                padding: '16px',
                border: 'none',
                borderRadius: '12px',
                // backgroundColor: '#fef3c7',
                margin: '16px 0',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-start',
              }}>
              <input
                type="checkbox"
                checked={acknowledgeCustomAuth}
                onChange={e => setAcknowledgeCustomAuth(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  marginTop: '2px',
                  cursor: 'pointer',
                }}
              />
              <div style={{ flex: 1, fontSize: '14px', fontFamily: 'Manrope' }}>
                <strong>Ack</strong> - Skip this check only if custom service account is used for authentication within
                cluster instead of GCP Workload Identity
              </div>
            </Box>
          )}

          <a onClick={handleHelpClick} style={{ color: '#194bfb' }}>
            {' '}
            How to install?{' '}
          </a>
          <p href="" style={{ color: '#194bfb' }}>
            I want to talk with CyberQ shield expert
          </p>
          {/* <a href="https://seclogic-inc.gitbook.io/admin-guide/overview/onboarding"> How to install? </a> */}
        </Box>

        <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
          {/* <button
            className="KubernetesOn__Cancel"
            onClick={() => {
              handleClosePopup();
            }}>
            Cancel
          </button> */}
        </div>
      </>

      <div style={styles.buttonRow}>
        <div></div>
        <button
          onClick={handleNext}
          disabled={isFetchingAddOnData || !addOnData || (criticalChecksFailed && !acknowledgeCustomAuth)}
          style={{
            ...styles.button,
            ...(isFetchingAddOnData || !addOnData || (criticalChecksFailed && !acknowledgeCustomAuth)
              ? styles.buttonDisabled
              : styles.buttonPrimary),
          }}
          onMouseEnter={e => Object.assign(e.target.style, styles.buttonPrimaryHover)}
          onMouseLeave={e => Object.assign(e.target.style, styles.buttonPrimary)}>
          Next
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  // Step 2: Eligibility Script UI
  const renderEligibilityScript = () => (
    <div style={styles.contentCard}>
      <div style={styles.cardHeader}>
        <div style={{ ...styles.cardIcon, ...styles.cardIconBlue }}>{/* <Shield color="#3b82f6" size={20} /> */} 2</div>
        <div>
          <p style={styles.cardTitle}>Run Eligibility Script</p>
          <p style={styles.cardSubtitle}>Verify your cluster meets the requirements</p>
        </div>
      </div>

      {errorValue?.error ? (
        <div style={{ ...styles.alertBox, ...styles.alertBoxError }}>
          <div style={styles.alertIcon}>
            <CloudOff size={20} color="#ef4444" />
          </div>
          <div>
            <div style={{ ...styles.alertTitle, color: '#7f1d1d' }}>Error</div>
            <div style={{ ...styles.alertText, color: '#b91c1c' }}>{errorValue.message}</div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ ...styles.alertBox, ...styles.alertBoxInfo }}>
            <div style={styles.alertIcon}>
              <HelpCircle size={20} color="#3b82f6" />
            </div>
            <div>
              <div style={{ ...styles.alertTitle, color: '#1e3a8a' }}>Instructions</div>
              <div style={{ ...styles.alertText, color: '#1d4ed8' }}>
                Copy and paste the command below into your cloud shell or terminal to check cluster eligibility
              </div>
            </div>
          </div>

          {isGeneratingCommand ? (
            <div style={styles.skeletonContainer}>
              <div style={styles.skeleton}></div>
            </div>
          ) : (
            <CommandBlock
              command={command}
              commandId="eligibility"
              isLoading={isEligibilityLoading}
              copiedStates={copiedStates}
              setCopiedStates={setCopiedStates}
            />
          )}
        </div>
      )}

      <div style={styles.buttonRow}>
        <button
          onClick={handleBack}
          style={{ ...styles.button, ...styles.buttonSecondary }}
          onMouseEnter={e => Object.assign(e.target.style, styles.buttonSecondaryHover)}
          onMouseLeave={e => Object.assign(e.target.style, styles.buttonSecondary)}>
          <ArrowLeft size={16} />
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!command || errorValue.error || isEligibilityLoading}
          style={{
            ...styles.button,
            ...(!command || errorValue.error || isEligibilityLoading ? styles.buttonDisabled : styles.buttonPrimary),
          }}
          onMouseEnter={e => {
            if (command && !errorValue.error) {
              Object.assign(e.target.style, styles.buttonPrimaryHover);
            }
          }}
          onMouseLeave={e => {
            if (command && !errorValue.error) {
              Object.assign(e.target.style, styles.buttonPrimary);
            }
          }}>
          Next
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );

  // Add this helper function in your component
  const getStepConfiguration = useCallback(() => {
    const steps = [];
    let stepNumber = 1;

    // Only add resource deployment step if CloudFormation is NOT selected
    if (!cloudFormationOption) {
      // Default resource deployment (not CloudFormation)
      const resourceCommand = isConnected
        ? selectedRowData?.cloud_provider === 'gcp'
          ? gcpDeboardingCommands?.data?.resource_deboarding_command
          : deployResourcesCommand?.data?.command?.resource_deboarding_command
        : selectedRowData?.cloud_provider === 'gcp'
        ? gcpResourceOnboarding?.data?.onboarding_command
        : deployResourcesCommand?.data?.command;

      // Only add if resource command exists
      if (resourceCommand) {
        steps.push({
          type: 'resource',
          stepNumber: stepNumber,
          command: resourceCommand,
          commandId: 'deployResources',
          title: !isConnected
            ? `Step ${stepNumber}: Deploy ${selectedClusterRowData?.cloud_provider === 'aws' ? 'AWS' : 'GCP'} Resources`
            : `Step ${stepNumber}: Remove ${
                selectedClusterRowData?.cloud_provider === 'aws' ? 'AWS' : 'GCP'
              } Resources`,
          isLoading: isDeployResourcesLoading || isFetchingGcpDeboardingCommands || isFetchingGCPResourceOnboarding,
        });
        stepNumber++;
      }
    } else if (
      cloudFormationOption &&
      cloudFormationItems.length > 0 &&
      selectedClusterRowData?.cloud_provider === 'aws' &&
      !isConnected
    ) {
      // CloudFormation steps - dynamically add based on available items (1, 2, or 3)
      cloudFormationItems.forEach(item => {
        steps.push({
          type: 'cloudformation',
          stepNumber: stepNumber,
          command: item.link,
          commandId: item.id,
          title: `Step ${stepNumber}: Execute ${item.title} Command`,
          isLoading: isFetchingCloudFormation,
        });
        stepNumber++;
      });
    }

    // api-security-t1 CloudFormation steps (provisions the forwarder/builder IRSA roles).
    // Deliberately NOT gated on cloudFormationOption above — api-security-t1 only has a
    // CloudFormation onboarding path (no wget/resource equivalent), so these apply whenever the
    // client has opted into API Security, regardless of which method the base cluster itself
    // uses. Empty when apiSecurityEnabled is off (the query above is disabled in that case), so
    // no separate switch check is needed here.
    if (!isConnected && apiSecurityT1CloudFormationItems.length > 0) {
      apiSecurityT1CloudFormationItems.forEach(item => {
        steps.push({
          type: 'apisecurity-t1-cloudformation',
          stepNumber: stepNumber,
          command: item.link,
          commandId: item.id,
          title: `Step ${stepNumber}: Execute ${item.title} Command`,
          isLoading: isFetchingApiSecurityT1CloudFormation,
        });
        stepNumber++;
      });
    }

    // Helm is always last if available
    const helmCommand = isConnected
      ? selectedRowData?.cloud_provider === 'gcp'
        ? gcpDeboardingCommands?.data?.helm_deboarding_command
        : deployResourcesCommand?.data?.command?.helm_command
      : helmInstallCommand;

    if (helmCommand) {
      steps.push({
        type: 'helm',
        stepNumber: stepNumber,
        command: helmCommand,
        commandId: 'helmInstall',
        title: !isConnected ? `Step ${stepNumber}: Install Helm Chart` : `Step ${stepNumber}: Uninstall Helm Chart`,
        isLoading: isHelmLoading || isFetchingGcpDeboardingCommands || isDeployResourcesLoading,
      });
      stepNumber++;
    }

    // API Security is a SEPARATE chart and therefore a separate block. It must run AFTER the
    // operator's command: the operator ships Tetragon and the cilium.io CRDs these policies need,
    // so a combined block would hide a real ordering requirement.
    if (!isConnected && apiSecurityCommand) {
      steps.push({
        type: 'apisecurity',
        stepNumber: stepNumber,
        command: apiSecurityCommand,
        commandId: 'apiSecurityInstall',
        title: `Step ${stepNumber}: Install API Security Chart`,
        isLoading: isHelmLoading,
      });
    }

    return steps;
  }, [
    cloudFormationOption,
    cloudFormationItems,
    isConnected,
    selectedRowData?.cloud_provider,
    selectedClusterRowData?.cloud_provider,
    gcpDeboardingCommands,
    deployResourcesCommand,
    gcpResourceOnboarding,
    helmInstallCommand,
    isDeployResourcesLoading,
    isFetchingGcpDeboardingCommands,
    isFetchingGCPResourceOnboarding, // Added here
    isFetchingCloudFormation,
    isHelmLoading,
    // Without these the memo keeps its previous value and the API Security block never renders,
    // however many times the command is regenerated.
    apiSecurityCommand,
    // Same reasoning as apiSecurityCommand above: the items memo (not apiSecurityEnabled itself)
    // is what determines whether/what to render, and it already resolves to [] when the switch
    // is off (the underlying query's `enabled` covers that).
    apiSecurityT1CloudFormationItems,
    isFetchingApiSecurityT1CloudFormation,
    // apiSecurityEnabled is deliberately NOT a dependency: this callback reads apiSecurityCommand,
    // never the switch itself. The bug it looks like it should fix -- turning the switch off leaving
    // the install command on screen -- is fixed at the switch instead, by clearing appliedApiSec.
    // That changes the query key, so apiSecurityCommand goes undefined and the block disappears.
  ]);

  // Use the configuration in your component
  const stepConfiguration = useMemo(() => getStepConfiguration(), [getStepConfiguration]);
  // Step 3: Deployment Method UI
  // const renderDeploymentMethod = () => (
  //   <div style={styles.contentCard}>
  //     <div style={styles.cardHeader}>
  //       <div style={{ ...styles.cardIcon, ...styles.cardIconGreen }}>3</div>
  //       <div>
  //         <h2 style={styles.cardTitle}>Choose Deployment Method</h2>
  //         <p style={styles.cardSubtitle}>Select the approach that best fits your environment</p>
  //       </div>
  //     </div>

  //     <div style={styles.radioGroup}>
  //       <div
  //         style={{
  //           ...styles.radioOption,
  //           ...styles.radioOptionSelected,
  //           cursor: 'not-allowed',
  //           opacity: 0.7,
  //         }}>
  //         <div style={styles.radioContent}>
  //           <div
  //             style={{
  //               ...styles.radioButton,
  //               ...styles.radioButtonSelected,
  //             }}>
  //             <div style={styles.radioButtonDot} />
  //           </div>
  //           <div style={styles.radioText}>
  //             <div style={styles.radioTitle}>
  //               Deploy {selectedClusterRowData?.cloud_provider === 'aws' ? 'AWS' : 'GCP'} Resources and Helm Separately
  //             </div>
  //             <div style={styles.radioDescription}>
  //               Two-step deployment process - ideal for public clusters with AWS authentication enabled
  //             </div>
  //             <div style={styles.badgeContainer}></div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     <div style={styles.buttonRow}>
  //       <button
  //         onClick={handleBack}
  //         style={{ ...styles.button, ...styles.buttonSecondary }}
  //         onMouseEnter={e => Object.assign(e.target.style, styles.buttonSecondaryHover)}
  //         onMouseLeave={e => Object.assign(e.target.style, styles.buttonSecondary)}>
  //         <ArrowLeft size={16} />
  //         Back
  //       </button>
  //       <button
  //         onClick={handleNext}
  //         style={{ ...styles.button, ...styles.buttonPrimary }}
  //         onMouseEnter={e => Object.assign(e.target.style, styles.buttonPrimaryHover)}
  //         onMouseLeave={e => Object.assign(e.target.style, styles.buttonPrimary)}>
  //         Next
  //         <ArrowRight size={16} />
  //       </button>
  //     </div>
  //   </div>
  // );

  // Step 4: Deployment Commands UI
  const renderDeploymentCommands = () => (
    <div style={styles.contentCard}>
      <div style={styles.cardHeader}>
        {!isConnected && <div style={{ ...styles.cardIcon, ...styles.cardIconPurple }}>3</div>}

        <div>
          <p style={styles.cardTitle}>{!isConnected ? 'Execute Deployment Commands' : 'Execute Deboarding Commands'}</p>
          <p style={styles.cardSubtitle}>
            {!isConnected
              ? 'Run the commands below to complete your installation'
              : 'Run the commands below to disconnect your cluster and remove resources'}
          </p>
        </div>
        <Box flex={1} />

        {/* Only show switch for AWS when not connected */}
        {!isConnected && selectedClusterRowData?.cloud_provider === 'aws' && (
          <div style={styles.switchRow}>
            <span style={styles.switchLabel}>Onboard via CloudFormation</span>
            <Switch checked={cloudFormationOption} onChange={setCloudFormation} />
          </div>
        )}

        {/* API Security (T-1). Guarded on !isConnected like the switch above, because this same
            Step 3 card is reused by the DEBOARDING flow — without the guard the enable toggle
            appears while the user is disconnecting a cluster. */}
        {!isConnected && selectedClusterRowData?.cloud_provider === 'aws' && (
          <div style={styles.switchRow}>
            <span style={styles.switchLabel}>Enable API Security</span>
            <Switch
              checked={apiSecurityEnabled}
              onChange={next => {
                setApiSecurityEnabled(next);
                // Clearing the committed payload is what actually removes the command block.
                // Without it the generated command survives the toggle going off, and the user is
                // handed an install command for a capability they just declined.
                if (!next) setAppliedApiSec(null);
              }}
            />
          </div>
        )}
      </div>

      {/* Configuration for API Security. Inline styles, not Tailwind: there is not one className in
          this file, and the shadcn <Input> used elsewhere brings token-driven spacing that does not
          match this card. */}
      {!isConnected && apiSecurityEnabled && (
        <div style={styles.apiSecPanel}>
          <p style={styles.cardSubtitle}>
            Monitors only the backend pods you name below. Deploys as a second Helm chart, after the
            operator.
          </p>

          <label style={styles.apiSecLabel} htmlFor="apisec-namespace">
            Backend namespace
          </label>
          <input
            id="apisec-namespace"
            style={styles.textInput}
            value={apiSecForm.namespace}
            placeholder="e.g. production"
            onChange={e => setApiSecForm(f => ({ ...f, namespace: e.target.value }))}
          />

          <label style={styles.apiSecLabel} htmlFor="apisec-selector">
            Backend Service name
          </label>
          <input
            id="apisec-selector"
            style={styles.textInput}
            value={apiSecForm.serviceName}
            placeholder="e.g. cspm-backend"
            onChange={e => setApiSecForm(f => ({ ...f, serviceName: e.target.value }))}
          />
          <p style={styles.apiSecHint}>
            The Kubernetes Service already fronting this backend — its pod selector is resolved
            automatically, so there's nothing else to type here.
          </p>

          <label style={styles.apiSecLabel} htmlFor="apisec-storageclass">
            Baseline storage class
          </label>
          <input
            id="apisec-storageclass"
            style={styles.textInput}
            value={apiSecForm.storageClass}
            placeholder="gp2"
            onChange={e => setApiSecForm(f => ({ ...f, storageClass: e.target.value }))}
          />

          <label style={styles.apiSecLabel} htmlFor="apisec-storagesize">
            Baseline volume size
          </label>
          <input
            id="apisec-storagesize"
            style={styles.textInput}
            value={apiSecForm.storageSize}
            placeholder="10Gi"
            onChange={e => setApiSecForm(f => ({ ...f, storageSize: e.target.value }))}
          />

          {/* Commits the form. The command is only regenerated on click — keying the query on the
              raw inputs would refetch on every keystroke. */}
          <button
            type="button"
            style={styles.apiSecButton}
            disabled={!apiSecForm.namespace || !apiSecForm.serviceName}
            onClick={() =>
              setAppliedApiSec({
                enabled: true,
                storage_class: apiSecForm.storageClass || 'gp2',
                storage_size: apiSecForm.storageSize || '10Gi',
                targets: [
                  {
                    namespace: apiSecForm.namespace.trim(),
                    serviceName: apiSecForm.serviceName.trim(),
                  },
                ],
              })
            }>
            Generate API Security Command
          </button>
        </div>
      )}

      <div>
        <div>
          <div style={styles.spacer}>
            {/* Render all steps dynamically */}
            {stepConfiguration.map((step, index) => (
              <div key={step.commandId} style={{ marginBottom: index < stepConfiguration.length - 1 ? '24px' : '0' }}>
                <CommandBlock
                  copiedStates={copiedStates}
                  setCopiedStates={setCopiedStates}
                  command={step.command}
                  commandId={step.commandId}
                  title={step.title}
                  isLoading={step.isLoading}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.buttonRow}>
        {!isConnected ? (
          <button
            onClick={handleBack}
            style={{ ...styles.button, ...styles.buttonSecondary }}
            onMouseEnter={e => Object.assign(e.target.style, styles.buttonSecondaryHover)}
            onMouseLeave={e => Object.assign(e.target.style, styles.buttonSecondary)}>
            <ArrowLeft size={16} />
            Back
          </button>
        ) : (
          <div />
        )}
        <button
          onClick={!isConnected ? updateEnvironment : handleDisconnectCluster}
          style={{
            ...styles.button,
            ...(isDeployResourcesLoading || isHelmLoading || (cloudFormationOption && isFetchingCloudFormation)
              ? styles.buttonDisabled
              : styles.buttonSuccess),
          }}
          onMouseEnter={e => Object.assign(e.target.style, styles.buttonSuccessHover)}
          onMouseLeave={e => Object.assign(e.target.style, styles.buttonSuccess)}>
          {disconnectLoaded ? (
            <span>
              <Spinner animation="border" size="sm" />
              &nbsp;&nbsp;&nbsp;Disconnecting Cluster...
            </span>
          ) : (
            <>
              <CheckCircle size={16} />
              {!isConnected ? 'Verify Installation' : 'Verify Deboarding'}
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: .5;
            }
          }
        `}
      </style>
      <div style={styles.wrapper}>
        <div
          className="KubernetesOn__Title"
          style={{ display: 'flex', justifyContent: 'space-between', padding: '25px 15px 5px', alignItems: 'start' }}>
          <div style={styles.header}>
            <p style={styles.title}>{!isConnected ? 'Cluster Onboarding' : 'Cluster Deboarding'}</p>
            <p style={styles.subtitle}>
              {!isConnected
                ? 'Set up your cluster with CyberQ Shield in just a few steps'
                : 'Disconnect your cluster and remove CyberQ Shield in just a few steps'}
            </p>
          </div>
          <CircularCloseButton onClick={handleClosePopup} />
        </div>

        {/* If disconnecting (isConnected === false), show only step 3 */}
        {isConnected ? (
          <div style={styles.transition}>{renderDeploymentCommands()}</div>
        ) : (
          <>
            <StepIndicator />
            <div style={styles.transition}>
              {activeStep === 0 && renderClusterAddons()}
              {activeStep === 1 && renderEligibilityScript()}
              {/* {activeStep === 2 && renderDeploymentMethod()} */}
              {activeStep === 2 && renderDeploymentCommands()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const CommandBlock = ({
  command,
  commandId,
  title,
  isLoading,
  copiedStates,
  setCopiedStates,
  containerHeight = '200px',
  codeHeight = '120px',
  // Optional, for callers that show a link rather than a shell command.
  headerLabel = 'Terminal',
  breakAll = false,
}) => {
  const isCopied = copiedStates[commandId];

  const copyTextToClipboard = useCallback(async (text, commandId) => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      setCopiedStates(prev => ({ ...prev, [commandId]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [commandId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // console.log(command, 'deployResourcesCommand', isLoading);
  return (
    <div style={styles.commandBlock}>
      {title && <div style={styles.commandTitle}>{title}</div>}
      <div style={{ ...styles.commandContainer, maxHeight: containerHeight }}>
        <div style={styles.commandHeader}>
          <div style={styles.commandHeaderLeft}>
            <Terminal size={16} color="#10b981" />
            <span style={styles.commandHeaderTitle}>{headerLabel}</span>
          </div>
          <button
            onClick={() => copyTextToClipboard(command, commandId)}
            style={{
              ...styles.copyButton,
              ...(isCopied ? {} : {}),
            }}
            disabled={isLoading || !command}
            onMouseEnter={e => {
              if (!isCopied) {
                Object.assign(e.currentTarget.style, styles.copyButtonHover);
              }
            }}
            onMouseLeave={e => {
              if (!isCopied) {
                Object.assign(e.currentTarget.style, styles.copyButton);
              }
            }}>
            {isCopied ? (
              <>
                <CheckCircle size={14} color="#10b981" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy size={14} />
                <span style={{ ...styles.spanCopy }}>Copy</span>
              </>
            )}
          </button>
        </div>
        {isLoading ? (
          <div style={styles.skeletonContainer}>
            <div style={styles.skeleton}></div>
          </div>
        ) : (
          <pre
            style={{ ...styles.commandPre, maxHeight: codeHeight, ...(breakAll ? { wordBreak: 'break-all' } : {}) }}
            className="CustomScrollbar">
            {command}
          </pre>
        )}
      </div>
    </div>
  );
};
